import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckCircle2, 
  Circle, 
  Clock, 
  TrendingUp,
  Target,
  Zap,
  Calendar
} from 'lucide-react';
import { todosAPI, trackingAPI } from '../utils/api';
import { formatRelativeTime, getTimeRemaining } from '../utils/dateUtils';

const Dashboard = ({ categories, tags, userProfile }) => {
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    pending: 0,
    inProgress: 0,
    overdue: 0
  });
  const [recentTodos, setRecentTodos] = useState([]);
  const [todayTodos, setTodayTodos] = useState([]);
  const [trackingStats, setTrackingStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [todosRes, trackingRes] = await Promise.all([
        todosAPI.getAll(),
        trackingAPI.getStats({ 
          startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        })
      ]);

      const todos = todosRes.data;
      
      // Calculate stats
      const newStats = {
        total: todos.length,
        completed: todos.filter(t => t.status === 'completed').length,
        pending: todos.filter(t => t.status === 'pending').length,
        inProgress: todos.filter(t => t.status === 'in-progress').length,
        overdue: todos.filter(t => {
          if (!t.dueDate || t.status === 'completed') return false;
          return new Date(t.dueDate) < new Date();
        }).length
      };

      setStats(newStats);

      // Get recent todos
      const sorted = [...todos]
        .sort((a, b) => b.createdAt - a.createdAt)
        .slice(0, 5);
      setRecentTodos(sorted);

      // Get today's todos
      const today = new Date().toISOString().split('T')[0];
      const todaysTodos = todos.filter(t => 
        t.dueDate && t.dueDate.split('T')[0] === today && t.status !== 'completed'
      );
      setTodayTodos(todaysTodos);

      setTrackingStats(trackingRes.data);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, color }) => (
    <div className="stat-card" style={{ borderLeftColor: color }}>
      <div className="stat-icon" style={{ backgroundColor: `${color}15` }}>
        <Icon size={24} style={{ color }} />
      </div>
      <div className="stat-content">
        <h3>{title}</h3>
        <div className="stat-value">{value}</div>
        {subtitle && <p className="stat-subtitle">{subtitle}</p>}
      </div>
    </div>
  );

  if (loading) {
    return <div className="page-loading">Loading dashboard...</div>;
  }

  const completionRate = stats.total > 0 
    ? Math.round((stats.completed / stats.total) * 100) 
    : 0;

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>Your productivity at a glance</p>
      </div>

      <div className="stats-grid">
        <StatCard
          icon={Target}
          title="Total Tasks"
          value={stats.total}
          subtitle={`${completionRate}% completion rate`}
          color="#6366f1"
        />
        <StatCard
          icon={CheckCircle2}
          title="Completed"
          value={stats.completed}
          subtitle="Great progress!"
          color="#10b981"
        />
        <StatCard
          icon={Zap}
          title="In Progress"
          value={stats.inProgress}
          subtitle="Keep going"
          color="#f59e0b"
        />
        <StatCard
          icon={Clock}
          title="Pending"
          value={stats.pending}
          subtitle={stats.overdue > 0 ? `${stats.overdue} overdue` : 'All on track'}
          color={stats.overdue > 0 ? '#ef4444' : '#64748b'}
        />
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-card">
          <div className="card-header">
            <h2>Today's Focus</h2>
            <Link to="/todos/today" className="card-link">View all</Link>
          </div>
          <div className="card-content">
            {todayTodos.length === 0 ? (
              <div className="empty-state">
                <Calendar size={48} />
                <p>No tasks due today</p>
                <Link to="/todos" className="btn-primary">Add a task</Link>
              </div>
            ) : (
              <div className="todo-list-compact">
                {todayTodos.map(todo => (
                  <div key={todo.id} className="todo-item-compact">
                    <Circle size={18} />
                    <div className="todo-info">
                      <h4>{todo.title}</h4>
                      {todo.dueDate && (
                        <span className="time-remaining">
                          {getTimeRemaining(todo.dueDate)?.isOverdue 
                            ? 'Overdue' 
                            : formatRelativeTime(todo.dueDate)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="dashboard-card">
          <div className="card-header">
            <h2>Recent Activity</h2>
            <Link to="/todos" className="card-link">View all</Link>
          </div>
          <div className="card-content">
            {recentTodos.length === 0 ? (
              <div className="empty-state">
                <TrendingUp size={48} />
                <p>No recent activity</p>
              </div>
            ) : (
              <div className="todo-list-compact">
                {recentTodos.map(todo => (
                  <div key={todo.id} className="todo-item-compact">
                    {todo.status === 'completed' ? (
                      <CheckCircle2 size={18} className="completed-icon" />
                    ) : (
                      <Circle size={18} />
                    )}
                    <div className="todo-info">
                      <h4 className={todo.status === 'completed' ? 'completed' : ''}>
                        {todo.title}
                      </h4>
                      <span className="todo-time">
                        {formatRelativeTime(todo.createdAt)}
                      </span>
                    </div>
                    <span className={`status-badge ${todo.status}`}>
                      {todo.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {trackingStats && (
        <div className="dashboard-card full-width">
          <div className="card-header">
            <h2>This Week's Tracking</h2>
            <Link to="/analytics" className="card-link">View details</Link>
          </div>
          <div className="card-content">
            <div className="tracking-summary">
              <div className="tracking-stat">
                <h3>{trackingStats.totalEntries}</h3>
                <p>Activities Logged</p>
              </div>
              <div className="tracking-stat">
                <h3>{Math.round(trackingStats.totalDuration / 60)}h</h3>
                <p>Total Time</p>
              </div>
              <div className="tracking-stat">
                <h3>{trackingStats.averageDuration}m</h3>
                <p>Avg. Duration</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
